// Zona "Notificar novedad" dentro del PanelHausAdmin.
// Permite al admin HAUS enviar un email de novedades a los invitados del
// pliego (y a emails extra), eligiendo una plantilla predefinida o
// redactando desde cero. Envía un mail por destinatario (nominal, no BCC)
// invocando la Edge Function `enviar-correo`.

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { PaperPlaneTilt, Users, CheckCircle, XCircle, Envelope, ClockClockwise, ArrowCounterClockwise } from '@phosphor-icons/react'
import { supabase } from '../lib/supabase'
import { PLANTILLAS, PLANTILLA_BY_ID, rellenarPlantilla } from '../plantillas-notificaciones'

const C = {
  text: '#2C2017', muted: '#7A6A5A', border: '#DDD5C8',
  olive: '#8D9A2E', amber: '#C98A2E', coral: '#D4614A',
  bg: '#FFFFFF', bgAlt: '#FAF7F3',
}
const CARD = { background: C.bg, borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(59,47,47,0.07)', border: `1px solid ${C.border}` }
const FONT = "'DM Sans', sans-serif"

const ROLES_ORDER = ['arquitectura', 'ingenieria', 'promotor', 'contratista', 'otro']
const ROL_LABEL = {
  arquitectura: 'Arquitectura',
  ingenieria:   'Ingeniería',
  promotor:     'Promotor',
  contratista:  'Contratista',
  otro:         'Otros',
}

const input = {
  width: '100%', padding: '0.5rem 0.6rem', borderRadius: 4,
  border: `1px solid ${C.border}`, fontFamily: FONT, fontSize: '0.85rem',
  color: C.text, background: C.bg, boxSizing: 'border-box',
}

// Palabras que indican que una etiqueta es una organización, no una persona
const CORPORATE_RE = /\b(Group|Grupo|S\.?L\.?|S\.?A\.?|SRL|LLC|Ltd|Studio|Ingenier[íi]a|Ingenieros|Arquitectos|Arquitectura|Consultores|Consulting|Equipo|Team|Associates|Partners)\b/i

// Extrae el nombre para saludar. Toma un string tipo "Marta Lamoglia Ros",
// "Fidel · IHD Ingenieros", "ARR Aires Mateus", "Arum Group" y devuelve
// "Marta", "Fidel", "Aires", "equipo de Arum Group".
function extraerNombre(txt) {
  if (!txt) return ''
  let t = txt.trim()
  if (!t) return ''
  // "Fidel · IHD Ingenieros" → quedarse con "Fidel"
  if (t.includes('·')) {
    const parte = t.split('·')[0].trim()
    if (parte) t = parte
  }
  // Si primera palabra son 2-4 letras TODAS MAYÚSCULAS → iniciales, saltarlas
  // ("ARR Aires Mateus" → "Aires Mateus")
  const words = t.split(/\s+/)
  if (words.length >= 2 && /^[A-ZÁÉÍÓÚÑ]{2,4}$/.test(words[0])) {
    t = words.slice(1).join(' ')
  }
  // Si lo que queda es una organización, usar "equipo de X"
  if (CORPORATE_RE.test(t)) return `equipo de ${t}`
  // Primera palabra como nombre
  return t.split(/\s+/)[0]
}

// Personaliza el body reemplazando {nombre}. Si no hay nombre, limpia
// "Hola {nombre}," → "Hola," para que no queden placeholders colgando.
function personalizarBody(body, nombre) {
  if (nombre) return body.replace(/\{nombre\}/g, nombre)
  return body.replace(/Hola\s+\{nombre\},/g, 'Hola,').replace(/\{nombre\}/g, '')
}

// Parsea el textarea "Emails extra". Acepta tres formatos por línea:
//   · Nombre: email@dom.com
//   · Nombre <email@dom.com>
//   · email@dom.com  (sin nombre → saludo "Hola,")
// Separadores entre entradas: salto de línea, coma o punto y coma.
function parseExtras(txt) {
  const out = []
  const seen = new Set()
  for (const linea of (txt || '').split(/\n/)) {
    for (let seg of linea.split(/[,;]/)) {
      seg = seg.trim()
      if (!seg) continue
      let nombre = ''
      let email = ''
      // "Nombre <email>"
      let m = seg.match(/^(.+?)\s*<\s*([^<>\s]+@[^<>\s]+\.[^<>\s]+)\s*>$/)
      if (m) { nombre = m[1].trim(); email = m[2].toLowerCase() }
      if (!email) {
        // "Nombre: email"  (el nombre puede contener acentos/espacios, el email no)
        m = seg.match(/^(.+?)\s*:\s*([^\s<>:]+@[^\s<>:]+\.[^\s<>:]+)$/)
        if (m) { nombre = m[1].trim(); email = m[2].toLowerCase() }
      }
      if (!email) {
        // solo email
        const em = seg.toLowerCase()
        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) { email = em }
      }
      if (!email || seen.has(email)) continue
      seen.add(email)
      out.push({ email, nombre })
    }
  }
  return out
}

export default function ZonaNotificarNovedad({ projectId, projectName }) {
  const [loading, setLoading]     = useState(true)
  const [invitados, setInvitados] = useState([])
  const [checked, setChecked]     = useState({})  // email → bool
  const [extraEmails, setExtraEmails] = useState('')
  const [plantillaId, setPlantillaId] = useState('vacia')
  const [subject, setSubject]     = useState('')
  const [body, setBody]           = useState('')
  const [ctaUrl, setCtaUrl]       = useState('')
  const [ctaLabel, setCtaLabel]   = useState('')
  const [enviando, setEnviando]   = useState(false)
  const [resultado, setResultado] = useState(null)
  const [historico, setHistorico] = useState([])
  const [historicoLoading, setHistoricoLoading] = useState(false)
  const [historicoOpen, setHistoricoOpen] = useState(false)
  const [programar, setProgramar] = useState(false)
  const [scheduledAt, setScheduledAt] = useState('')  // valor del input datetime-local
  const [programados, setProgramados] = useState([])
  const [previewOpen, setPreviewOpen] = useState(false)

  // 1 · Carga invitaciones activas del proyecto
  const cargar = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    const { data, error } = await supabase
      .from('pliego_invitaciones')
      .select('email_destinatario, pliego_rol, etiqueta, nombre_contacto, activo')
      .eq('project_id', projectId)
      .eq('activo', true)
      .order('pliego_rol', { ascending: true })
    if (error) {
      console.error('[ZonaNotificarNovedad] carga invitaciones:', error)
      setInvitados([])
    } else {
      // Dedupe por email (puede haber duplicadas si re-invitaron)
      const seen = new Set()
      const out = []
      for (const r of (data || [])) {
        const em = (r.email_destinatario || '').trim().toLowerCase()
        if (!em || seen.has(em)) continue
        seen.add(em)
        out.push({
          email: em,
          rol: r.pliego_rol || 'otro',
          etiqueta: r.etiqueta || '',
          nombre_contacto: r.nombre_contacto || '',
        })
      }
      setInvitados(out)
    }
    setLoading(false)
  }, [projectId])

  useEffect(() => { cargar() }, [cargar])

  // 1b · Carga histórico de últimos envíos del proyecto (contexto novedad_pliego)
  const cargarHistorico = useCallback(async () => {
    if (!projectId) return
    setHistoricoLoading(true)
    const { data, error } = await supabase
      .from('correos_enviados')
      .select('id, destinatario, asunto, cuerpo_texto, estado, enviado_at, error_detalle')
      .eq('contexto', 'novedad_pliego')
      .eq('referencia_id', projectId)
      .order('enviado_at', { ascending: false })
      .limit(50)
    if (error) {
      console.error('[ZonaNotificarNovedad] carga histórico:', error)
      setHistorico([])
    } else {
      setHistorico(data || [])
    }
    setHistoricoLoading(false)
  }, [projectId])

  useEffect(() => { cargarHistorico() }, [cargarHistorico])

  // 1c · Carga correos programados pendientes
  const cargarProgramados = useCallback(async () => {
    if (!projectId) return
    const { data, error } = await supabase
      .from('correos_programados')
      .select('id, scheduled_at, asunto, destinatarios, estado, procesado_at, resultado')
      .eq('project_id', projectId)
      .in('estado', ['pending', 'procesando', 'error'])
      .order('scheduled_at', { ascending: true })
      .limit(50)
    if (!error) setProgramados(data || [])
  }, [projectId])

  useEffect(() => { cargarProgramados() }, [cargarProgramados])

  // Agrupa envíos por "campaña" (mismo asunto + ventana de 2 minutos)
  const campanas = useMemo(() => {
    const groups = []
    for (const row of historico) {
      const t = new Date(row.enviado_at).getTime()
      const last = groups[groups.length - 1]
      if (last && last.asunto === row.asunto && Math.abs(t - last.tsFirst) < 120000) {
        last.items.push(row)
        last.tsFirst = Math.min(last.tsFirst, t)
      } else {
        groups.push({ asunto: row.asunto, tsFirst: t, items: [row] })
      }
    }
    return groups
  }, [historico])

  // Reenviar: rellena los campos del formulario con los datos de un envío previo
  function reutilizar(campana) {
    const first = campana.items[0]
    setSubject(first.asunto || '')
    setBody(first.cuerpo_texto || '')
    // Marcar en checkboxes los destinatarios que están en el pliego
    const emails = new Set(campana.items.map(it => (it.destinatario || '').toLowerCase()))
    const next = {}
    const extras = []
    for (const inv of invitados) {
      if (emails.has(inv.email)) next[inv.email] = true
    }
    setChecked(next)
    // Los emails fuera del pliego van al textarea extra
    for (const em of emails) {
      if (!invitados.some(i => i.email === em)) extras.push(em)
    }
    setExtraEmails(extras.join('\n'))
    // Scroll al principio del card
    try { document.getElementById('notif-form-top')?.scrollIntoView({ behavior: 'smooth', block: 'start' }) } catch {}
  }

  // 2 · Al cambiar plantilla, rellenar campos + preseleccionar destinatarios
  function aplicarPlantilla(id) {
    setPlantillaId(id)
    const p = PLANTILLA_BY_ID[id]
    if (!p) return
    const appUrl = typeof window !== 'undefined' ? window.location.origin : ''
    const rell = rellenarPlantilla(p, { proyecto: projectName || '', app_url: appUrl })
    setSubject(rell.subject)
    setBody(rell.body)
    setCtaUrl(rell.cta_url || '')
    setCtaLabel(rell.cta_label || '')
    // Pre-selección por rol
    if (p.preselect_roles && p.preselect_roles.length > 0) {
      const next = {}
      for (const inv of invitados) {
        if (p.preselect_roles.includes(inv.rol)) next[inv.email] = true
      }
      setChecked(next)
    } else {
      setChecked({})
    }
  }

  // 3 · Añade equipo HAUS (user_profiles.organization = 'HAUS Healthy Buildings')
  async function addEquipoHaus() {
    try {
      const { data: haus, error } = await supabase
        .from('user_profiles')
        .select('user_id, full_name, organization')
        .ilike('organization', '%HAUS%')
      if (error) throw error
      if (!haus || haus.length === 0) {
        alert('No se encontraron perfiles con organization HAUS.')
        return
      }
      // user_profiles no guarda emails · pedirlos vía nombres y comparar
      // contra la lista de invitados o avisar al admin.
      const nombres = haus.map(h => h.full_name).filter(Boolean)
      const matches = invitados.filter(inv =>
        nombres.some(n => inv.etiqueta.toLowerCase().includes(n.toLowerCase())
                       || n.toLowerCase().includes(inv.etiqueta.toLowerCase()))
      )
      if (matches.length === 0) {
        alert('Equipo HAUS no está invitado al pliego. Añade los emails manualmente en el campo "Emails extra".')
        return
      }
      const next = { ...checked }
      for (const m of matches) next[m.email] = true
      setChecked(next)
    } catch (e) {
      alert('Error buscando equipo HAUS: ' + (e.message || e))
    }
  }

  // 4 · Lista final de destinatarios = checked + extras parseados
  const extrasParsed = useMemo(() => parseExtras(extraEmails), [extraEmails])
  const destinatarios = useMemo(() => {
    const set = new Set()
    for (const [em, on] of Object.entries(checked)) {
      if (on) set.add(em.trim().toLowerCase())
    }
    for (const { email } of extrasParsed) set.add(email)
    return Array.from(set)
  }, [checked, extrasParsed])

  // Mapa email → nombre, combinando invitaciones del pliego y nombres escritos
  // en el textarea "Emails extra" (formato "Nombre: email" o "Nombre <email>")
  const nombreByEmail = useMemo(() => {
    const m = {}
    for (const i of invitados) m[i.email] = i.nombre_contacto || i.etiqueta || ''
    for (const { email, nombre } of extrasParsed) {
      if (nombre && !m[email]) m[email] = nombre
    }
    return m
  }, [invitados, extrasParsed])

  // 5 · Agrupar invitados por rol para la lista
  const invitadosByRol = useMemo(() => {
    const g = {}
    for (const inv of invitados) {
      const r = inv.rol || 'otro'
      if (!g[r]) g[r] = []
      g[r].push(inv)
    }
    return g
  }, [invitados])

  // 6 · Envío · 1 llamada por destinatario (mail nominal)
  async function enviar() {
    if (destinatarios.length === 0) {
      alert('Añade al menos un destinatario.')
      return
    }
    if (!subject.trim() || !body.trim()) {
      alert('Asunto y cuerpo son obligatorios.')
      return
    }
    // Los paréntesis () dentro del asunto rompen el encoded-word MIME y Outlook
    // Classic acaba mostrando el body como texto plano. Los sustituimos por · .
    const subjectSafe = subject.trim().replace(/[()]/g, '·').replace(/·\s*·/g, '·')

    // ─── MODO PROGRAMADO ───
    if (programar) {
      if (!scheduledAt) {
        alert('Elige fecha y hora para programar el envío.')
        return
      }
      const schedDate = new Date(scheduledAt)
      if (schedDate.getTime() <= Date.now()) {
        alert('La fecha/hora debe estar en el futuro.')
        return
      }
      if (!window.confirm(`Se programan ${destinatarios.length} correos para el ${schedDate.toLocaleString()}. ¿Continuar?`)) {
        return
      }
      setEnviando(true)
      const { data: userData } = await supabase.auth.getUser()
      // Una fila por destinatario con body YA personalizado (evita placeholders
      // en Edge Function; el cron no necesita saber de personalización).
      // Prioridad: nombre_contacto (campo explícito) > etiqueta (fallback).
      const rows = destinatarios.map(to => {
        const nombre = extraerNombre(nombreByEmail[to])
        return {
          programado_por: userData?.user?.id || null,
          project_id: projectId,
          scheduled_at: schedDate.toISOString(),
          destinatarios: [to],
          asunto: subjectSafe,
          body_text: personalizarBody(body, nombre),
          cta_url: ctaUrl || null,
          cta_label: ctaLabel || null,
          contexto: 'novedad_pliego',
        }
      })
      const { error } = await supabase.from('correos_programados').insert(rows)
      setEnviando(false)
      if (error) {
        setResultado({ ok: [], err: [{ email: '(programación)', error: error.message }] })
      } else {
        setResultado({ ok: destinatarios, err: [], programado: true, when: schedDate })
        setProgramar(false)
        setScheduledAt('')
      }
      cargarProgramados()
      return
    }

    // ─── MODO INMEDIATO ───
    if (!window.confirm(`Se van a enviar ${destinatarios.length} correos. ¿Continuar?`)) {
      return
    }
    setEnviando(true)
    setResultado(null)
    const okList  = []
    const errList = []
    for (const to of destinatarios) {
      const nombre = extraerNombre(nombreByEmail[to])
      const bodyPersonal = personalizarBody(body, nombre)
      try {
        const { data, error } = await supabase.functions.invoke('enviar-correo', {
          body: {
            to,
            subject: subjectSafe,
            body_text: bodyPersonal,
            cta_url: ctaUrl || undefined,
            cta_label: ctaLabel || undefined,
            contexto: 'novedad_pliego',
            referencia_id: projectId,
          },
        })
        if (error) throw new Error(error.message || 'invoke failed')
        if (!data?.ok) throw new Error(data?.error || 'respuesta inesperada')
        okList.push(to)
      } catch (e) {
        errList.push({ email: to, error: e?.message || String(e) })
      }
    }
    setEnviando(false)
    setResultado({ ok: okList, err: errList })
    cargarHistorico()
  }

  // Cancelar un correo programado pendiente
  async function cancelarProgramado(id) {
    if (!window.confirm('¿Cancelar este envío programado?')) return
    const { error } = await supabase
      .from('correos_programados')
      .update({ estado: 'cancelado' })
      .eq('id', id)
      .eq('estado', 'pending')
    if (error) {
      alert('Error cancelando: ' + error.message)
    } else {
      cargarProgramados()
    }
  }

  if (!projectId) return null

  return (
    <div style={{ ...CARD, fontFamily: FONT }} id="notif-form-top">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <PaperPlaneTilt size={18} color={C.olive} />
        <h3 style={{ margin: 0, fontSize: '1rem', color: C.text, fontWeight: 700 }}>
          Notificar novedad a los invitados del pliego
        </h3>
      </div>

      {loading ? (
        <div style={{ color: C.muted, fontSize: '0.85rem' }}>Cargando invitaciones…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>

          {/* ────── Columna izquierda: destinatarios + plantilla ────── */}
          <div>
            <div style={{ marginBottom: '0.8rem' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: C.muted, marginBottom: '0.25rem', fontWeight: 600 }}>
                Plantilla
              </label>
              <select
                value={plantillaId}
                onChange={e => aplicarPlantilla(e.target.value)}
                style={{ ...input, cursor: 'pointer' }}
              >
                {PLANTILLAS.map(p => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '0.4rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ fontSize: '0.75rem', color: C.muted, fontWeight: 600 }}>
                Destinatarios del pliego ({invitados.length})
              </label>
              <button
                onClick={addEquipoHaus}
                style={{ padding: '3px 8px', fontSize: '0.7rem', border: `1px solid ${C.olive}`, background: 'white', color: C.olive, borderRadius: 3, cursor: 'pointer', fontFamily: FONT, display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
              >
                <Users size={11} /> Añadir equipo HAUS
              </button>
            </div>

            <div style={{ maxHeight: 240, overflowY: 'auto', border: `1px solid ${C.border}`, borderRadius: 4, padding: '0.5rem', background: C.bgAlt }}>
              {ROLES_ORDER.filter(r => invitadosByRol[r]).map(r => (
                <div key={r} style={{ marginBottom: '0.6rem' }}>
                  <div style={{ fontSize: '0.68rem', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>
                    {ROL_LABEL[r] || r}
                  </div>
                  {invitadosByRol[r].map(inv => (
                    <label key={inv.email} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.78rem', color: C.text, padding: '0.15rem 0.2rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={!!checked[inv.email]}
                        onChange={e => setChecked({ ...checked, [inv.email]: e.target.checked })}
                      />
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {inv.nombre_contacto
                          ? <><b>{inv.nombre_contacto}</b> <span style={{ color: C.muted, fontWeight: 400 }}>· {inv.email}{inv.etiqueta ? ` · ${inv.etiqueta}` : ''}</span></>
                          : <><span style={{ color: C.coral, fontStyle: 'italic' }}>(sin nombre)</span> <span style={{ color: C.muted }}>· {inv.email}{inv.etiqueta ? ` · ${inv.etiqueta}` : ''}</span></>
                        }
                      </span>
                    </label>
                  ))}
                </div>
              ))}
              {invitados.length === 0 && (
                <div style={{ color: C.muted, fontSize: '0.8rem', textAlign: 'center', padding: '0.5rem' }}>
                  No hay invitaciones activas para este proyecto.
                </div>
              )}
            </div>

            <label style={{ display: 'block', fontSize: '0.75rem', color: C.muted, fontWeight: 600, margin: '0.8rem 0 0.25rem' }}>
              Emails extra (uno por línea · acepta <code style={{ fontSize: '0.72rem' }}>Nombre: email</code> para personalizar el saludo)
            </label>
            <textarea
              value={extraEmails}
              onChange={e => setExtraEmails(e.target.value)}
              placeholder={'Marta: m.lamoglia@haus-hb.com\nRicard: r.santamaria@haus-hb.com\notro@dominio.com'}
              rows={3}
              style={{ ...input, resize: 'vertical', fontFamily: FONT }}
            />

            <div style={{ marginTop: '0.6rem', fontSize: '0.8rem', color: C.text, background: C.bgAlt, padding: '0.5rem 0.7rem', borderRadius: 4, border: `1px solid ${C.border}` }}>
              <Envelope size={13} style={{ verticalAlign: '-2px', marginRight: '0.3rem', color: C.olive }} />
              Se enviarán <b>{destinatarios.length}</b> correo{destinatarios.length === 1 ? '' : 's'} (un mail nominal por destinatario).
            </div>
          </div>

          {/* ────── Columna derecha: asunto + cuerpo + preview ────── */}
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', color: C.muted, fontWeight: 600, marginBottom: '0.25rem' }}>
              Asunto
            </label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              style={input}
              placeholder="Asunto del correo"
            />

            <label style={{ display: 'block', fontSize: '0.75rem', color: C.muted, fontWeight: 600, margin: '0.6rem 0 0.25rem' }}>
              Cuerpo
            </label>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={10}
              style={{ ...input, resize: 'vertical', fontFamily: FONT, lineHeight: 1.4 }}
              placeholder="Cuerpo del mensaje..."
            />

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.5rem', marginTop: '0.6rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', color: C.muted, fontWeight: 600, marginBottom: '0.25rem' }}>
                  URL del botón (opcional)
                </label>
                <input
                  type="url"
                  value={ctaUrl}
                  onChange={e => setCtaUrl(e.target.value)}
                  style={input}
                  placeholder="https://…"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', color: C.muted, fontWeight: 600, marginBottom: '0.25rem' }}>
                  Texto del botón
                </label>
                <input
                  type="text"
                  value={ctaLabel}
                  onChange={e => setCtaLabel(e.target.value)}
                  style={input}
                  placeholder="Acceder"
                />
              </div>
            </div>

            {/* Preview de saludos personalizados */}
            {destinatarios.length > 0 && body.includes('{nombre}') && (
              <div style={{ marginTop: '0.8rem', padding: '0.5rem 0.7rem', background: C.bgAlt, borderRadius: 4, border: `1px solid ${C.border}` }}>
                <button
                  onClick={() => setPreviewOpen(!previewOpen)}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.text, fontSize: '0.78rem', fontFamily: FONT, padding: 0, display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}
                >
                  Preview de saludos ({destinatarios.length}) <span style={{ color: C.muted }}>{previewOpen ? '▲' : '▼'}</span>
                </button>
                {previewOpen && (() => {
                  return (
                    <div style={{ marginTop: '0.4rem', fontSize: '0.75rem', color: C.text, maxHeight: 180, overflowY: 'auto' }}>
                      {destinatarios.map(em => {
                        const n = extraerNombre(nombreByEmail[em])
                        const saludo = n ? `Hola ${n},` : 'Hola,'
                        return (
                          <div key={em} style={{ padding: '2px 0', borderBottom: `1px solid ${C.border}` }}>
                            <span style={{ color: C.muted }}>{em}</span>
                            <span style={{ margin: '0 0.4rem', color: C.muted }}>→</span>
                            <span style={{ color: n ? C.olive : C.amber, fontWeight: 600 }}>{saludo}</span>
                          </div>
                        )
                      })}
                      <div style={{ marginTop: '0.4rem', fontSize: '0.7rem', color: C.muted, fontStyle: 'italic' }}>
                        Para corregir un saludo, edita el campo "Nombre contacto" del invitado en el bloque de Invitaciones (botón <b>editar</b>).
                      </div>
                    </div>
                  )
                })()}
              </div>
            )}

            {/* Toggle programar envío + datetime picker */}
            <div style={{ marginTop: '0.8rem', padding: '0.5rem 0.7rem', background: C.bgAlt, borderRadius: 4, border: `1px solid ${C.border}` }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.82rem', color: C.text, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={programar}
                  onChange={e => setProgramar(e.target.checked)}
                />
                <ClockClockwise size={14} color={programar ? C.olive : C.muted} />
                <b>Programar envío</b> para una fecha/hora futura
              </label>
              {programar && (
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={e => setScheduledAt(e.target.value)}
                  min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                  style={{ ...input, marginTop: '0.4rem' }}
                />
              )}
            </div>

            <button
              onClick={enviar}
              disabled={enviando || destinatarios.length === 0 || !subject.trim() || !body.trim()}
              style={{
                marginTop: '0.6rem', width: '100%', padding: '0.65rem', borderRadius: 4,
                background: enviando ? C.muted : C.olive, color: 'white', border: 'none',
                fontWeight: 700, fontSize: '0.9rem', fontFamily: FONT,
                cursor: enviando ? 'wait' : 'pointer',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem',
                opacity: (destinatarios.length === 0 || !subject.trim() || !body.trim()) ? 0.5 : 1,
              }}
            >
              {programar ? <ClockClockwise size={16} /> : <PaperPlaneTilt size={16} />}
              {enviando
                ? (programar ? 'Programando…' : 'Enviando…')
                : (programar
                    ? `Programar envío a ${destinatarios.length} destinatario${destinatarios.length === 1 ? '' : 's'}`
                    : `Enviar a ${destinatarios.length} destinatario${destinatarios.length === 1 ? '' : 's'}`)}
            </button>

            {resultado && (() => {
              // Reusamos el nombreByEmail del scope del componente (incluye pliego + extras con nombre)
              const renderDest = em => {
                const n = nombreByEmail[em]
                return n ? <><b>{n}</b> <span style={{ color: C.muted }}>({em})</span></> : em
              }
              return (
              <div style={{ marginTop: '0.6rem', fontSize: '0.8rem', color: C.text }}>
                {resultado.programado ? (
                  <div style={{ color: C.olive, marginBottom: '0.3rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginBottom: '0.3rem' }}>
                      <ClockClockwise size={14} weight="fill" /> Programado para <b>{resultado.when.toLocaleString()}</b>
                    </div>
                    <ul style={{ margin: '0.2rem 0 0 1.1rem', padding: 0, color: C.text, fontSize: '0.75rem' }}>
                      {resultado.ok.map((em, i) => <li key={i}>{renderDest(em)}</li>)}
                    </ul>
                  </div>
                ) : (
                  <div style={{ color: C.olive, marginBottom: '0.3rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginBottom: '0.3rem' }}>
                      <CheckCircle size={14} weight="fill" /> Enviados correctamente: <b>{resultado.ok.length}</b>
                    </div>
                    <ul style={{ margin: '0.2rem 0 0 1.1rem', padding: 0, color: C.text, fontSize: '0.75rem' }}>
                      {resultado.ok.map((em, i) => <li key={i}>{renderDest(em)}</li>)}
                    </ul>
                  </div>
                )}
                {resultado.err.length > 0 && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', color: C.coral, marginBottom: '0.2rem' }}>
                      <XCircle size={14} weight="fill" /> Fallidos: <b>{resultado.err.length}</b>
                    </div>
                    <ul style={{ margin: '0.2rem 0 0 1.1rem', padding: 0, color: C.coral, fontSize: '0.75rem' }}>
                      {resultado.err.map((e, i) => (
                        <li key={i}>{renderDest(e.email)} · {e.error}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
              )
            })()}
          </div>

        </div>
      )}

      {/* ────── Programados pendientes ────── */}
      {programados.filter(p => p.estado === 'pending').length > 0 && (
        <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: `1px solid ${C.border}` }}>
          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: C.text, marginBottom: '0.5rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
            <ClockClockwise size={15} color={C.amber} />
            Envíos programados pendientes
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem', fontFamily: FONT }}>
            <thead>
              <tr style={{ textAlign: 'left', color: C.muted, borderBottom: `1px solid ${C.border}` }}>
                <th style={{ padding: '6px 8px', fontWeight: 600 }}>Programado para</th>
                <th style={{ padding: '6px 8px', fontWeight: 600 }}>Asunto</th>
                <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'center' }}>Dest.</th>
                <th style={{ padding: '6px 8px' }}></th>
              </tr>
            </thead>
            <tbody>
              {programados.filter(p => p.estado === 'pending').map(p => {
                const d = new Date(p.scheduled_at)
                const fecha = `${d.getDate().toString().padStart(2,'0')}/${(d.getMonth()+1).toString().padStart(2,'0')} ${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`
                return (
                  <tr key={p.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                    <td style={{ padding: '6px 8px', color: C.amber, fontWeight: 600, whiteSpace: 'nowrap' }}>{fecha}</td>
                    <td style={{ padding: '6px 8px', color: C.text, maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.asunto}>{p.asunto}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>{(p.destinatarios || []).length}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                      <button
                        onClick={() => cancelarProgramado(p.id)}
                        title="Cancelar este envío programado"
                        style={{ padding: '3px 8px', fontSize: '0.72rem', border: `1px solid ${C.coral}`, background: 'white', color: C.coral, borderRadius: 3, cursor: 'pointer', fontFamily: FONT }}
                      >
                        Cancelar
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ────── Histórico de envíos ────── */}
      <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: `1px solid ${C.border}` }}>
        <button
          onClick={() => { setHistoricoOpen(!historicoOpen); if (!historicoOpen) cargarHistorico() }}
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.text, fontSize: '0.85rem', fontWeight: 700, fontFamily: FONT, padding: 0, display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
        >
          <ClockClockwise size={15} color={C.olive} />
          Últimos envíos {historico.length > 0 ? `· ${campanas.length} campaña${campanas.length === 1 ? '' : 's'} / ${historico.length} correo${historico.length === 1 ? '' : 's'}` : ''}
          <span style={{ color: C.muted, fontWeight: 400 }}>{historicoOpen ? '▲' : '▼'}</span>
        </button>

        {historicoOpen && (
          <div style={{ marginTop: '0.6rem' }}>
            {historicoLoading && <div style={{ color: C.muted, fontSize: '0.8rem' }}>Cargando…</div>}
            {!historicoLoading && historico.length === 0 && (
              <div style={{ color: C.muted, fontSize: '0.8rem', fontStyle: 'italic' }}>
                Aún no se han enviado notificaciones en este proyecto.
              </div>
            )}
            {!historicoLoading && campanas.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem', fontFamily: FONT }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: C.muted, borderBottom: `1px solid ${C.border}` }}>
                    <th style={{ padding: '6px 8px', fontWeight: 600 }}>Fecha</th>
                    <th style={{ padding: '6px 8px', fontWeight: 600 }}>Asunto</th>
                    <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'center' }}>Dest.</th>
                    <th style={{ padding: '6px 8px', fontWeight: 600, textAlign: 'center' }}>Estado</th>
                    <th style={{ padding: '6px 8px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {campanas.map((c, idx) => {
                    const okCount = c.items.filter(i => i.estado === 'ok').length
                    const errCount = c.items.length - okCount
                    const d = new Date(c.tsFirst)
                    const fecha = `${d.getDate().toString().padStart(2,'0')}/${(d.getMonth()+1).toString().padStart(2,'0')} ${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`
                    return (
                      <tr key={idx} style={{ borderBottom: `1px solid ${C.border}` }}>
                        <td style={{ padding: '6px 8px', color: C.muted, whiteSpace: 'nowrap' }}>{fecha}</td>
                        <td style={{ padding: '6px 8px', color: C.text, maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.asunto}>{c.asunto}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>{c.items.length}</td>
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                          {errCount === 0 ? (
                            <span style={{ color: C.olive, fontWeight: 700 }}>✓ {okCount}</span>
                          ) : (
                            <span style={{ color: C.coral, fontWeight: 700 }}>✓ {okCount} · ✗ {errCount}</span>
                          )}
                        </td>
                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                          <button
                            onClick={() => reutilizar(c)}
                            title="Rellenar formulario con los datos de este envío (puedes modificarlo antes de reenviar)"
                            style={{ padding: '3px 8px', fontSize: '0.72rem', border: `1px solid ${C.olive}`, background: 'white', color: C.olive, borderRadius: 3, cursor: 'pointer', fontFamily: FONT, display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}
                          >
                            <ArrowCounterClockwise size={11} /> Reutilizar
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
